/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PlatformAdminUIState, PlatformAdminUIThunkType } from '../../../../store/store';
import ChangeNameActionView from './ChangeNameAction.view';
import {
	showSnackbar,
	SnackbarAction,
	SnackbarType
} from '../../../../shared/views/SnackbarNotification/store/SnackbarNotification.actions';
import { UserActionTypes } from '../../store/UserActionTypes';
import { ActionMenuItemProps } from '../../../../shared/views/ActionMenu/ActionMenu';
import { ClientPersonChangeNameByAdminRequest, ClientPersonExtended } from '@DigitaleDoerfer/digitale-doerfer-api';
import { changeNameByAdmin } from '../../store/User.actions';

interface Props extends ActionMenuItemProps {
	currentFirstName?: string;
	currentLastName?: string;
	user: ClientPersonExtended | null;
	showSnackbar: (snackbarAction: SnackbarAction) => void;
	changeName: (changeNameRequestParams: ClientPersonChangeNameByAdminRequest) => void;
}

class ChangeNameActionContainer extends Component<Props> {
	constructor(props: Props) {
		super(props);

		this.handleSave = this.handleSave.bind(this);
	}

	handleSave(newFirstName: string, newLastName: string): void {
		const { user, showSnackbar, changeName } = this.props;
		const personId = user?.id;
		if (personId !== undefined) {
			changeName({
				firstName: newFirstName,
				lastName: newLastName,
				personId
			});
		} else {
			showSnackbar({
				type: SnackbarType.SNACKBAR_ERROR,
				message: 'Benutzer hat keine ID.'
			});
		}
	}

	render(): JSX.Element {
		const { currentFirstName, currentLastName, renderMenuItem } = this.props;
		return (
			<ChangeNameActionView
				renderMenuItem={renderMenuItem}
				currentFirstName={currentFirstName || ''}
				currentLastName={currentLastName || ''}
				handleSave={this.handleSave}
			/>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: PlatformAdminUIState) => {
	return {
		currentFirstName: state.user.detail.user?.firstName,
		currentLastName: state.user.detail.user?.lastName,
		user: state.user.detail.user
	};
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: PlatformAdminUIThunkType<UserActionTypes>) => {
	return {
		showSnackbar: (snackbarAction: SnackbarAction): Promise<void> => dispatch(showSnackbar(snackbarAction)),
		changeName: (changeNameRequestParams: ClientPersonChangeNameByAdminRequest): Promise<void> =>
			dispatch(changeNameByAdmin(changeNameRequestParams))
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeNameActionContainer);
